import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings, Calendar } from 'lucide-react';
import { PropertyReportWithStats } from '../../../types/models';
import { format } from 'date-fns';
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog';
import ShareReportDialog from '../Shared/ShareReportDialog';
import { FirebaseService } from '../../../services/FirebaseService';
import { useAuth } from '../../Auth/AuthContext';
import './ReportsGrid.css';

interface ReportsGridProps {
  reports: PropertyReportWithStats[];
  onViewReport?: (reportId: string) => void;
  onViewPDF?: (reportId: string) => void;
  onReportDeleted?: () => void; // Callback to refresh reports list
  onReportShared?: () => void; // Callback to refresh reports list
}

export const ReportsGrid: React.FC<ReportsGridProps> = ({ reports, onViewReport, onViewPDF, onReportDeleted, onReportShared }) => {
  const navigate = useNavigate();

  if (reports.length === 0) {
    return (
      <div className="reports-empty">
        <h3>No Reports Found</h3>
        <p>
          No property reports match your current search criteria.
          Try adjusting your search terms or check back later.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="reports-grid">
        {reports.map((report) => (
          <ReportCard 
            key={report.id} 
            report={report} 
            onGeneratePDF={() => navigate(`/pdf-editor/${report.id}`)}
            onViewReport={onViewReport}
            onViewPDF={onViewPDF}
            onReportDeleted={onReportDeleted}
            onReportShared={onReportShared}
          />
        ))}
      </div>
    </>
  );
};

interface ReportCardProps {
  report: PropertyReportWithStats;
  onGeneratePDF: () => void;
  onViewReport?: (reportId: string) => void;
  onViewPDF?: (reportId: string) => void;
  onReportDeleted?: () => void;
  onReportShared?: () => void;
}

const ReportCard: React.FC<ReportCardProps> = ({ report, onGeneratePDF, onViewReport, onViewPDF, onReportDeleted, onReportShared }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  
  const entryCount = report.entryCount;

  // Close settings menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(event.target as Node)) {
        setSettingsMenuOpen(false);
      }
    };

    if (settingsMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [settingsMenuOpen]);

  const handleViewEntries = () => {
    if (onViewReport) {
      onViewReport(report.id);
    } else {
      console.log('View entries for report:', report.id);
    }
  };

  const handleViewPDF = () => {
    if (onViewPDF) {
      onViewPDF(report.id);
    } else {
      navigate(`/pdf-editor/${report.id}`);
    }
  };

  const handleDeleteReport = async () => {
    if (!user) {
      console.error('User not authenticated');
      return;
    }

    try {
      await FirebaseService.deleteReport(user.uid, report.id);
      console.log('Report deleted successfully');
      if (onReportDeleted) {
        onReportDeleted(); // Refresh the reports list
      }
    } catch (error) {
      console.error('Failed to delete report:', error);
      throw error; // Let the dialog handle the error
    }
  };

  return (
    <>
      <div className="report-card">
        <div className="report-card-header">
          <h3 className="report-title">{report.title}</h3>
          
          {/* Settings/Gear Icon */}
          <div className="settings-menu-container" ref={settingsMenuRef}>
            <button 
              className="settings-btn"
              onClick={() => setSettingsMenuOpen(!settingsMenuOpen)}
              title="Report Settings"
            >
              <Settings size={16} />
            </button>
            
            {settingsMenuOpen && (
              <div className="settings-dropdown">
                <button 
                  className="settings-menu-item"
                  onClick={() => {
                    setShareDialogOpen(true);
                    setSettingsMenuOpen(false);
                  }}
                >
                  Share
                </button>
                <button 
                  className="settings-menu-item delete"
                  onClick={() => {
                    setDeleteDialogOpen(true);
                    setSettingsMenuOpen(false);
                  }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
          
          <div className="report-date">
            <span className="calendar-icon"><Calendar size={14} /></span>
            <span className="date-text">
              {format(report.createdAt, 'MMMM d, yyyy')} • {format(report.createdAt, 'h:mm a')}
            </span>
          </div>
        </div>

        <div className="report-card-content">
          <div className="report-stats">
            <span className="entry-count">{entryCount} {entryCount === 1 ? 'entry' : 'entries'}</span>
          </div>

          <div className="report-card-actions">
            <button onClick={handleViewEntries} className="action-btn btn-view">
              View Entries
            </button>
            <button onClick={handleViewPDF} className="action-btn btn-edit">
              View PDF
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        reportTitle={report.title}
        onConfirm={handleDeleteReport}
        onCancel={() => setDeleteDialogOpen(false)}
      />

      {/* Share Report Dialog */}
      <ShareReportDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        reportId={report.id}
        reportTitle={report.title}
        onShared={onReportShared}
      />
    </>
  );
};
